import React, { useEffect, useRef, useState } from 'react';
import { Popover } from '@mui/material';

export type HoverPopoverTriggerApi<T> = {
  openAt: (anchorEl: HTMLElement, item: T) => void;
  scheduleClose: () => void;
};

export type HoverPopoverProps<T> = {
  trigger: (api: HoverPopoverTriggerApi<T>) => React.ReactNode;
  children: (item: T) => React.ReactNode;
  closeDelayMs?: number;
};

function HoverPopover<T>({
  trigger,
  children,
  closeDelayMs = 150,
}: HoverPopoverProps<T>) {
  const [item, setItem] = useState<T | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const closeTimerRef = useRef<number | null>(null);

  const clearCloseTimer = () => {
    if (closeTimerRef.current != null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const openAt = (nextAnchorEl: HTMLElement, nextItem: T) => {
    clearCloseTimer();
    setAnchorEl(nextAnchorEl);
    setItem(nextItem);
  };

  const scheduleClose = () => {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => {
      setAnchorEl(null);
      setItem(null);
      closeTimerRef.current = null;
    }, closeDelayMs);
  };

  const close = () => {
    clearCloseTimer();
    setAnchorEl(null);
    setItem(null);
  };

  useEffect(() => clearCloseTimer, []);

  return (
    <>
      {trigger({ openAt, scheduleClose })}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        disableRestoreFocus
        disableAutoFocus
        disableEnforceFocus
        disableScrollLock
        sx={{ pointerEvents: 'none' }}
        slotProps={{
          paper: {
            onMouseEnter: clearCloseTimer,
            onMouseLeave: scheduleClose,
            sx: {
              pointerEvents: 'auto',
              mt: 0.5,
              p: 1,
              minWidth: 200,
              maxWidth: 300,
            },
          },
        }}
      >
        {item ? children(item) : null}
      </Popover>
    </>
  );
}

export default HoverPopover;
